import React, { useState } from 'react';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Button,
    Badge,
    Card,
    CardBody,
    Divider,
    IconButton,
    useToast,
    Spinner,
    Center,
    Icon,
    Link,
    Tooltip,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Input,
    Tag,
    TagLabel,
    TagCloseButton,
    Wrap,
    WrapItem
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import { FiArrowLeft, FiStar, FiTrash2, FiArchive, FiMail, FiDownload, FiPaperclip, FiExternalLink, FiPackage, FiCornerUpLeft, FiTag } from 'react-icons/fi';
import { format } from 'date-fns';
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor, brandConfig } from "../../brandConfig";
import CommunicationTaskList from './CommunicationTaskList';
import { ConvertToProjectModal } from '../../components/ConvertToProjectModal';

const GET_INBOUND_EMAIL_QUERY = gql`
    query GetInboundEmail($id: ID!) {
        inboundEmail(id: $id) {
            id
            messageId
            subject
            from
            fromName
            fromFull
            to
            toFull
            cc
            ccFull
            bcc
            replyTo
            date
            textBody
            htmlBody
            isRead
            isStarred
            folder
            labels
            attachments {
                name
                contentType
                contentLength
                ipfsUrl
                ipfsHash
            }
            tasks {
                id
                title
                description
                status
                priority
                dueDate
                assignedTo {
                    id
                    fName
                    lName
                    email
                }
                notes
                completedAt
                createdAt
            }
        }
    }
`;

const MARK_AS_READ_MUTATION = gql`
    mutation MarkAsRead($id: ID!, $isRead: Boolean!) {
        markInboundEmailAsRead(id: $id, isRead: $isRead) {
            id
            isRead
        }
    }
`;

const STAR_EMAIL_MUTATION = gql`
    mutation StarEmail($id: ID!, $isStarred: Boolean!) {
        starInboundEmail(id: $id, isStarred: $isStarred) {
            id
            isStarred
        }
    }
`;

const ADD_EMAIL_LABEL_MUTATION = gql`
    mutation AddInboundEmailLabel($emailId: String!, $label: String!) {
        addInboundEmailLabel(emailId: $emailId, label: $label) {
            id
            labels
        }
    }
`;

const REMOVE_EMAIL_LABEL_MUTATION = gql`
    mutation RemoveInboundEmailLabel($emailId: String!, $label: String!) {
        removeInboundEmailLabel(emailId: $emailId, label: $label) {
            id
            labels
        }
    }
`;

const AVAILABLE_LABELS_QUERY = gql`
    query AvailableInboundEmailLabels {
        availableInboundEmailLabels
    }
`;

const MOVE_TO_FOLDER_MUTATION = gql`
    mutation MoveToFolder($id: ID!, $folder: String!) {
        moveInboundEmailToFolder(id: $id, folder: $folder) {
            id
            folder
        }
    }
`;

const DELETE_EMAIL_MUTATION = gql`
    mutation DeleteInboundEmail($id: ID!) {
        deleteInboundEmail(id: $id)
    }
`;

const InboundEmailDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const toast = useToast();

    // Consistent styling from brandConfig
    const bg = getColor("background.main");
    const cardGradientBg = getColor("background.cardGradient");
    const cardBorder = getColor("border.darkCard");
    const textPrimary = getColor("text.primaryDark");
    const textSecondary = getColor("text.secondaryDark");
    const textMuted = getColor("text.mutedDark");
    
    // Modal state for Convert to Project
    const { isOpen: isConvertModalOpen, onOpen: onConvertModalOpen, onClose: onConvertModalClose } = useDisclosure();
    
    // Modal state for Tag Management
    const { isOpen: isTagModalOpen, onOpen: onTagModalOpen, onClose: onTagModalClose } = useDisclosure();
    const [newLabel, setNewLabel] = useState('');

    const { data, loading, error, refetch } = useQuery(GET_INBOUND_EMAIL_QUERY, {
        variables: { id },
        skip: !id,
        onError: (err) => {
            console.error('Failed to fetch email:', err);
            console.error('GraphQL Errors:', err.graphQLErrors);
            console.error('Network Error:', err.networkError);
            toast({
                title: 'Error loading email',
                description: err.message || 'Unable to fetch email details',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    });

    const { data: availableLabelsData } = useQuery(AVAILABLE_LABELS_QUERY);
    
    const [starEmail] = useMutation(STAR_EMAIL_MUTATION);
    const [moveToFolder] = useMutation(MOVE_TO_FOLDER_MUTATION);
    const [addLabel] = useMutation(ADD_EMAIL_LABEL_MUTATION, {
        onCompleted: () => {
            toast({
                title: 'Tag added',
                status: 'success',
                duration: 2000,
            });
            refetch();
        },
    });
    
    const [removeLabel] = useMutation(REMOVE_EMAIL_LABEL_MUTATION, {
        onCompleted: () => {
            toast({
                title: 'Tag removed',
                status: 'success',
                duration: 2000,
            });
            refetch();
        },
    });
    
    const [deleteEmail] = useMutation(DELETE_EMAIL_MUTATION, {
        onCompleted: () => {
            toast({
                title: 'Email deleted',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            navigate('/emails');
        }
    });

    const handleStar = async () => {
        if (data?.inboundEmail) {
            await starEmail({
                variables: {
                    id: data.inboundEmail.id,
                    isStarred: !data.inboundEmail.isStarred
                }
            });
        }
    };

    const handleArchive = async () => {
        if (data?.inboundEmail) {
            const isArchived = data.inboundEmail.folder === 'Archived';
            const newFolder = isArchived ? 'Inbox' : 'Archived';
            
            await moveToFolder({
                variables: {
                    id: data.inboundEmail.id,
                    folder: newFolder
                }
            });
            
            toast({
                title: isArchived ? 'Email moved back to inbox' : 'Email archived',
                description: isArchived ? 'Email has been restored to your inbox' : 'Email has been moved to archive',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            
            // Navigate back to emails list after action
            setTimeout(() => {
                navigate('/emails');
            }, 1000);
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this email?')) {
            await deleteEmail({ variables: { id } });
        }
    };

    const handleAddLabel = async () => {
        if (newLabel && data?.inboundEmail) {
            await addLabel({
                variables: {
                    emailId: data.inboundEmail.id,
                    label: newLabel
                }
            });
            setNewLabel('');
        }
    };
    
    const handleRemoveLabel = async (label: string) => {
        if (data?.inboundEmail) {
            await removeLabel({
                variables: {
                    emailId: data.inboundEmail.id,
                    label: label
                }
            });
        }
    };
    
    const handleReply = () => {
        if (data?.inboundEmail) {
            const email = data.inboundEmail;
            
            // Build query parameters for the reply
            const params = new URLSearchParams({
                replyTo: email.from,
                replySubject: email.subject.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
                replyFromName: email.fromName || '',
                replyDate: email.date,
                replyBody: email.textBody || '',
                replyHtmlBody: email.htmlBody || ''
            });
            
            navigate(`/emails/new?${params.toString()}`);
        }
    };

    if (loading) {
        return (
            <Box minH="100vh" bg={bg} display="flex" flexDirection="column">
                <NavbarWithCallToAction />
                <Container maxW={{ base: "container.sm", md: "container.md", lg: "container.xl" }} px={{ base: 4, md: 8 }} py={{ base: 4, md: 8 }} flex="1">
                    <Center py={20}>
                        <Spinner size="xl" color={getColor('primaryBlue')} />
                    </Center>
                </Container>
                <FooterWithFourColumns />
            </Box>
        );
    }

    if (error || !data?.inboundEmail) {
        return (
            <Box minH="100vh" bg={bg} display="flex" flexDirection="column">
                <NavbarWithCallToAction />
                <Container maxW={{ base: "container.sm", md: "container.md", lg: "container.xl" }} px={{ base: 4, md: 8 }} py={{ base: 4, md: 8 }} flex="1">
                    <Card bg={cardGradientBg} border="1px solid" borderColor={cardBorder}>
                        <CardBody>
                            <Center py={10}>
                                <VStack spacing={4}>
                                    <Icon as={FiMail} boxSize={12} color={textMuted} />
                                    <Text color={textMuted} fontSize="lg">
                                        {error ? 'Error loading email' : 'Email not found'}
                                    </Text>
                                    {error && (
                                        <VStack spacing={2}>
                                            <Text color={textMuted} fontSize="sm" textAlign="center">
                                                {error.message}
                                            </Text>
                                            {id && (
                                                <Text color={textMuted} fontSize="xs" fontFamily="mono">
                                                    ID: {id}
                                                </Text>
                                            )}
                                        </VStack>
                                    )}
                                    <Button
                                        leftIcon={<FiArrowLeft />}
                                        onClick={() => navigate('/emails')}
                                        variant="ghost"
                                    >
                                        Back to Emails
                                    </Button>
                                </VStack>
                            </Center>
                        </CardBody>
                    </Card>
                </Container>
                <FooterWithFourColumns />
            </Box>
        );
    }

    const email = data.inboundEmail;

    return (
        <Box minH="100vh" bg={bg} display="flex" flexDirection="column">
            <NavbarWithCallToAction />
            
            <Container maxW={{ base: "container.sm", md: "container.md", lg: "container.xl" }} px={{ base: 4, md: 8 }} py={{ base: 4, md: 8 }} flex="1">
                <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                    <VStack align="stretch" spacing={{ base: 3, md: 0 }}>
                        <HStack spacing={{ base: 2, md: 3 }} flexWrap={{ base: "wrap", md: "nowrap" }} justify={{ base: "stretch", md: "flex-start" }}>
                            <Button
                                leftIcon={<FiArrowLeft />}
                                onClick={() => navigate('/emails')}
                                variant="outline"
                                borderColor={cardBorder}
                                color={textPrimary}
                                _hover={{ bg: cardGradientBg }}
                                size={{ base: "sm", md: "md" }}
                                width={{ base: "100%", md: "auto" }}
                            >
                                Back to Inbox
                            </Button>
                            <Button
                                leftIcon={<FiCornerUpLeft />}
                                onClick={handleReply}
                                variant="solid"
                                bg={getColor('primaryBlue')}
                                color="white"
                                _hover={{ bg: getColor('primaryBlueHover'), transform: "scale(1.02)" }}
                                _active={{ bg: getColor('primaryBlue') }}
                                px={{ base: 4, md: 6 }}
                                fontWeight="medium"
                                size={{ base: "sm", md: "md" }}
                                width={{ base: "100%", md: "auto" }}
                            >
                                Reply
                            </Button>
                            <Button 
                                leftIcon={<FiPackage />}
                                size={{ base: "sm", md: "lg" }}
                                variant="solid"
                                bg="green.500"
                                color="white"
                                _hover={{ bg: "green.600", transform: "scale(1.05)" }}
                                _active={{ bg: "green.700" }}
                                onClick={onConvertModalOpen}
                                px={{ base: 4, md: 8 }}
                                fontSize={{ base: "sm", md: "lg" }}
                                fontWeight="bold"
                                boxShadow="lg"
                                width={{ base: "100%", md: "auto" }}
                            >
                                Convert to Project
                            </Button>
                        </HStack>
                        <HStack spacing={{ base: 2, md: 3 }} justify={{ base: "center", md: "flex-end" }}>
                            <Tooltip 
                                label={email.isStarred ? "Unstar email" : "Star email"} 
                                placement="bottom"
                                bg={getColor('primaryBlue')}
                                color="white"
                                fontSize="sm"
                                px={3}
                                py={2}
                                borderRadius="md"
                                hasArrow
                            >
                                <IconButton
                                    aria-label="Star email"
                                    icon={<FiStar />}
                                    onClick={handleStar}
                                    variant="ghost"
                                    color={email.isStarred ? getColor('starYellow') : textMuted}
                                    _hover={{ 
                                        transform: "scale(1.1)",
                                        color: email.isStarred ? getColor('starYellowHover') : getColor('primaryBlue')
                                    }}
                                />
                            </Tooltip>
                            
                            <Tooltip 
                                label={email.folder === 'Archived' 
                                    ? "Move back to Inbox if tasks are not completed" 
                                    : "Archive email when all tasks are done"} 
                                placement="bottom"
                                bg={getColor('primaryBlue')}
                                color="white"
                                fontSize="sm"
                                px={3}
                                py={2}
                                borderRadius="md"
                                hasArrow
                            >
                                <IconButton
                                    aria-label={email.folder === 'Archived' ? "Unarchive email" : "Archive email"}
                                    icon={<FiArchive />}
                                    onClick={handleArchive}
                                    variant="ghost"
                                    color={email.folder === 'Archived' ? getColor('primaryBlue') : textMuted}
                                    _hover={{ 
                                        transform: "scale(1.1)",
                                        color: getColor('primaryBlue')
                                    }}
                                />
                            </Tooltip>
                            
                            <Tooltip 
                                label="Delete email" 
                                placement="bottom"
                                bg={getColor('status.error')}
                                color="white"
                                fontSize="sm"
                                px={3}
                                py={2}
                                borderRadius="md"
                                hasArrow
                            >
                                <IconButton
                                    aria-label="Delete email"
                                    icon={<FiTrash2 />}
                                    onClick={handleDelete}
                                    variant="ghost"
                                    color="red.500"
                                    _hover={{ 
                                        transform: "scale(1.1)",
                                        color: "red.600",
                                        bg: "red.50"
                                    }}
                                />
                            </Tooltip>
                        </HStack>
                    </VStack>

                    <Card bg={cardGradientBg} border="1px solid" borderColor={cardBorder}>
                        <CardBody p={{ base: 3, md: 6 }}>
                            <VStack align="stretch" spacing={{ base: 4, md: 6 }}>
                                {/* Subject and metadata */}
                                <VStack align="stretch" spacing={3}>
                                    <VStack align="start" spacing={{ base: 2, md: 3 }}>
                                        <Heading size={{ base: "md", md: "lg", lg: "xl" }} color={textPrimary}>
                                            {email.subject}
                                        </Heading>
                                        {email.folder && (
                                            <Badge colorScheme="blue" fontSize={{ base: "xs", md: "sm" }}>{email.folder}</Badge>
                                        )}
                                    </VStack>
                                    
                                    <HStack spacing={2} align="center">
                                        {email.labels?.length > 0 && (
                                            <Wrap spacing={2}>
                                                {email.labels.map((label: string) => (
                                                    <WrapItem key={label}>
                                                        <Tag
                                                            size="md"
                                                            bg="rgba(168, 85, 247, 0.2)"
                                                            color="#A855F7"
                                                            border="1px solid"
                                                            borderColor="rgba(168, 85, 247, 0.3)"
                                                        >
                                                            <TagLabel>{label}</TagLabel>
                                                        </Tag>
                                                    </WrapItem>
                                                ))}
                                            </Wrap>
                                        )}
                                        <Tooltip 
                                            label="Manage tags" 
                                            placement="top"
                                            bg={getColor('primaryBlue')}
                                            color="white"
                                            fontSize="sm"
                                            px={3}
                                            py={2}
                                            borderRadius="md"
                                            hasArrow
                                        >
                                            <IconButton
                                                aria-label="Manage tags"
                                                icon={<FiTag />}
                                                size="sm"
                                                variant="ghost"
                                                color={textMuted}
                                                _hover={{ 
                                                    bg: cardGradientBg, 
                                                    color: getColor('primaryBlue'),
                                                    transform: "scale(1.1)"
                                                }}
                                                onClick={onTagModalOpen}
                                            />
                                        </Tooltip>
                                    </HStack>
                                </VStack>

                                <Divider borderColor={cardBorder} />

                                {/* From/To information */}
                                <VStack align="stretch" spacing={{ base: 2, md: 3 }}>
                                    <VStack align="start" spacing={1}>
                                        <Text fontWeight="bold" color={textMuted} fontSize={{ base: "sm", md: "md" }}>From:</Text>
                                        <Text color={textPrimary} fontSize={{ base: "sm", md: "md" }} pl={{ base: 0, md: 4 }}>
                                            {email.fromFull || email.from}
                                        </Text>
                                    </VStack>
                                    <VStack align="start" spacing={1}>
                                        <Text fontWeight="bold" color={textMuted} fontSize={{ base: "sm", md: "md" }}>To:</Text>
                                        <VStack align="start" spacing={1} pl={{ base: 0, md: 4 }}>
                                            {email.toFull?.map((to: string, index: number) => (
                                                <Text key={index} color={textPrimary} fontSize={{ base: "sm", md: "md" }}>{to}</Text>
                                            )) || email.to?.map((to: string, index: number) => (
                                                <Text key={index} color={textPrimary} fontSize={{ base: "sm", md: "md" }}>{to}</Text>
                                            ))}
                                        </VStack>
                                    </VStack>
                                    {email.cc?.length > 0 && (
                                        <HStack align="start">
                                            <Text fontWeight="bold" color={textMuted} minW="80px">CC:</Text>
                                            <VStack align="start" spacing={1}>
                                                {email.ccFull?.map((cc: string, index: number) => (
                                                    <Text key={index} color={textPrimary}>{cc}</Text>
                                                )) || email.cc?.map((cc: string, index: number) => (
                                                    <Text key={index} color={textPrimary}>{cc}</Text>
                                                ))}
                                            </VStack>
                                        </HStack>
                                    )}
                                    <VStack align="start" spacing={1}>
                                        <Text fontWeight="bold" color={textMuted} fontSize={{ base: "sm", md: "md" }}>Date:</Text>
                                        <Text color={textPrimary} fontSize={{ base: "sm", md: "md" }} pl={{ base: 0, md: 4 }}>
                                            {format(new Date(email.date), 'PPpp')}
                                        </Text>
                                    </VStack>
                                </VStack>

                                <Divider borderColor={cardBorder} />

                                {/* Email content */}
                                <Box>
                                    {email.htmlBody ? (
                                        <Box
                                            dangerouslySetInnerHTML={{ __html: email.htmlBody }}
                                            color={textSecondary}
                                            sx={{
                                                '& a': { color: getColor('primaryBlue'), textDecoration: 'underline' },
                                                '& p': { marginBottom: '1em' },
                                                '& ul, & ol': { paddingLeft: '2em', marginBottom: '1em' },
                                                '& blockquote': { 
                                                    borderLeft: '3px solid',
                                                    borderColor: cardBorder,
                                                    paddingLeft: '1em',
                                                    marginLeft: '0',
                                                    color: textMuted
                                                }
                                            }}
                                        />
                                    ) : (
                                        <Text whiteSpace="pre-wrap" color={textSecondary}>
                                            {email.textBody || 'No content available'}
                                        </Text>
                                    )}
                                </Box>

                                {/* Attachments */}
                                {email.attachments?.length > 0 && (
                                    <>
                                        <Divider borderColor={cardBorder} />
                                        <VStack align="stretch" spacing={3}>
                                            <HStack>
                                                <Icon as={FiPaperclip} color={textMuted} />
                                                <Text fontWeight="bold" color={textMuted}>
                                                    Attachments ({email.attachments.length})
                                                </Text>
                                            </HStack>
                                            <VStack align="stretch" spacing={2}>
                                                {email.attachments.map((attachment: any, index: number) => (
                                                    <Box key={index}>
                                                        {attachment.ipfsUrl ? (
                                                            <Link
                                                                href={attachment.ipfsUrl}
                                                                isExternal
                                                                _hover={{ textDecoration: 'none' }}
                                                            >
                                                                <HStack 
                                                                    p={3} 
                                                                    bg={bg} 
                                                                    borderRadius="md"
                                                                    border="1px solid"
                                                                    borderColor={cardBorder}
                                                                    cursor="pointer"
                                                                    _hover={{ 
                                                                        bg: 'whiteAlpha.50',
                                                                        borderColor: getColor('primaryBlue') 
                                                                    }}
                                                                    transition="all 0.2s"
                                                                >
                                                                    <Icon as={FiDownload} color={getColor('primaryBlue')} />
                                                                    <VStack align="start" spacing={0} flex={1}>
                                                                        <Text color={textPrimary} fontWeight="medium">
                                                                            {attachment.name}
                                                                        </Text>
                                                                        <HStack spacing={2}>
                                                                            <Text fontSize="sm" color={textMuted}>
                                                                                {attachment.contentType}
                                                                            </Text>
                                                                            <Text fontSize="sm" color={textMuted}>
                                                                                • {Math.round(attachment.contentLength / 1024)} KB
                                                                            </Text>
                                                                            {attachment.ipfsHash && (
                                                                                <Text fontSize="xs" color={textMuted} fontFamily="mono">
                                                                                    • IPFS: {attachment.ipfsHash.substring(0, 8)}...
                                                                                </Text>
                                                                            )}
                                                                        </HStack>
                                                                    </VStack>
                                                                    <Icon as={FiExternalLink} color={textMuted} />
                                                                </HStack>
                                                            </Link>
                                                        ) : (
                                                            <HStack 
                                                                p={3} 
                                                                bg={bg} 
                                                                borderRadius="md"
                                                                border="1px solid"
                                                                borderColor={cardBorder}
                                                                opacity={0.7}
                                                            >
                                                                <Icon as={FiPaperclip} color={textMuted} />
                                                                <VStack align="start" spacing={0} flex={1}>
                                                                    <Text color={textPrimary}>
                                                                        {attachment.name}
                                                                    </Text>
                                                                    <Text fontSize="sm" color={textMuted}>
                                                                        {attachment.contentType} • {Math.round(attachment.contentLength / 1024)} KB
                                                                    </Text>
                                                                </VStack>
                                                                <Badge colorScheme="gray" fontSize="xs">
                                                                    Not uploaded
                                                                </Badge>
                                                            </HStack>
                                                        )}
                                                    </Box>
                                                ))}
                                            </VStack>
                                        </VStack>
                                    </>
                                )}
                            </VStack>
                        </CardBody>
                    </Card>
                    
                    {/* Tasks Section */}
                    {email.tasks && (
                        <Box mt={6}>
                            <CommunicationTaskList
                                tasks={email.tasks}
                                communicationId={id!}
                                communicationType="INBOUND_EMAIL"
                                onTasksUpdated={() => {
                                    // Refetch email to get updated tasks
                                    refetch();
                                }}
                                allowEdit={true}
                            />
                        </Box>
                    )}
                </VStack>
            </Container>

            <FooterWithFourColumns />
            
            {/* Convert to Project Modal */}
            {data?.inboundEmail && (
                <ConvertToProjectModal 
                    isOpen={isConvertModalOpen}
                    onClose={onConvertModalClose}
                    email={{
                        id: data.inboundEmail.id,
                        subject: data.inboundEmail.subject || '',
                        bodyMarkdown: data.inboundEmail.textBody || '',
                        from: data.inboundEmail.from || ''
                    }}
                />
            )}
            
            {data?.inboundEmail && (
                <Modal isOpen={isTagModalOpen} onClose={onTagModalClose} size="md">
                    <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(5px)" />
                    <ModalContent bg={cardGradientBg} border="1px solid" borderColor={cardBorder}>
                        <ModalHeader color={textPrimary}>
                            Manage Tags
                        </ModalHeader>
                        <ModalCloseButton color={textMuted} />
                        <ModalBody>
                            <VStack spacing={4} align="stretch">
                                <Text fontSize="sm" color={textSecondary} noOfLines={2}>
                                    <strong>Subject:</strong> {data.inboundEmail.subject}
                                </Text>
                                
                                {data.inboundEmail.labels && data.inboundEmail.labels.length > 0 && (
                                    <Box>
                                        <Text fontSize="sm" color={textMuted} mb={2}>Current tags:</Text>
                                        <Wrap spacing={2}>
                                            {data.inboundEmail.labels.map((label: string) => (
                                                <WrapItem key={label}>
                                                    <Tag
                                                        size="md"
                                                        bg="rgba(168, 85, 247, 0.2)"
                                                        color="#A855F7"
                                                        border="1px solid"
                                                        borderColor="rgba(168, 85, 247, 0.3)"
                                                    >
                                                        <TagLabel>{label}</TagLabel>
                                                        <TagCloseButton 
                                                            onClick={() => handleRemoveLabel(label)}
                                                        />
                                                    </Tag>
                                                </WrapItem>
                                            ))}
                                        </Wrap>
                                    </Box>
                                )}
                                
                                <Box>
                                    <Text fontSize="sm" color={textMuted} mb={2}>Add new tag:</Text>
                                    <HStack>
                                        <Input
                                            value={newLabel}
                                            onChange={(e) => setNewLabel(e.target.value)}
                                            placeholder="Enter tag name"
                                            size="sm"
                                            bg={bg}
                                            color={textPrimary}
                                            borderColor={cardBorder}
                                            _placeholder={{ color: textMuted }}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleAddLabel();
                                                }
                                            }}
                                        />
                                        <Button
                                            size="sm"
                                            onClick={handleAddLabel}
                                            bg={getColor('primaryBlue')}
                                            color="white"
                                            _hover={{ bg: getColor('primaryBlueHover') }}
                                            isDisabled={!newLabel.trim()}
                                        >
                                            Add
                                        </Button>
                                    </HStack>
                                </Box>
                                
                                {availableLabelsData?.availableInboundEmailLabels && availableLabelsData.availableInboundEmailLabels.length > 0 && (
                                    <Box>
                                        <Text fontSize="sm" color={textMuted} mb={2}>Quick add from existing tags:</Text>
                                        <Wrap spacing={2}>
                                            {availableLabelsData.availableInboundEmailLabels
                                                .filter((label: string) => 
                                                    !data.inboundEmail.labels?.includes(label)
                                                )
                                                .map((label: string) => (
                                                    <WrapItem key={label}>
                                                        <Tag
                                                            size="sm"
                                                            cursor="pointer"
                                                            bg={cardBorder}
                                                            color={textSecondary}
                                                            _hover={{ 
                                                                bg: "rgba(168, 85, 247, 0.2)",
                                                                color: "#A855F7"
                                                            }}
                                                            onClick={() => {
                                                                setNewLabel(label);
                                                                handleAddLabel();
                                                            }}
                                                        >
                                                            <TagLabel>{label}</TagLabel>
                                                        </Tag>
                                                    </WrapItem>
                                                ))}
                                        </Wrap>
                                    </Box>
                                )}
                            </VStack>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" onClick={onTagModalClose} color={textMuted}>
                                Done
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            )}
        </Box>
    );
};

export default InboundEmailDetail;